import { Controller } from "@hotwired/stimulus"

// Client-side search/filter for tables and lists.
//
// Usage:
//   <div data-controller="search-filter">
//     <input data-search-filter-target="input" data-action="input->search-filter#filter" placeholder="Search...">
//     <table>
//       <tbody data-search-filter-target="list">
//         <tr data-search-filter-target="item">
//           <td>Searchable text here</td>
//         </tr>
//       </tbody>
//     </table>
//   </div>

export default class extends Controller {
  static targets = ["input", "list", "item"]

  // On a big list (the portfolio dashboard runs this over 3,000+ rows) the
  // naive version re-queried the DOM, re-read every row's textContent and
  // wrote every row's style on EVERY keystroke — a forced reflow per letter.
  // Now: text is lowercased once and cached, keystrokes are debounced, and
  // only rows whose visibility actually changes get written (Phelipe
  // 2026-08-27).
  filter() {
    clearTimeout(this._filterTimer)
    this._filterTimer = setTimeout(() => this._applyFilter(), 150)
  }

  _applyFilter() {
    const query = this.inputTarget.value.toLowerCase().trim()
    const rows = this._rows()
    for (const row of rows) {
      const show = !query || row.text.includes(query)
      if (show !== row.shown) {
        row.el.style.display = show ? "" : "none"
        row.shown = show
      }
    }
  }

  // Cached per render. The list is re-rendered by a turbo-frame swap rather
  // than mutated in place, so a stale cache means a disconnected node — check
  // the first one and rebuild when the DOM moved under us.
  _rows() {
    if (!this._rowCache || !this._rowCache.length || !this._rowCache[0].el.isConnected) {
      this._rowCache = this.itemTargets.map(el => ({
        el, text: el.textContent.toLowerCase(), shown: el.style.display !== "none"
      }))
    }
    return this._rowCache
  }

  clear() {
    this.inputTarget.value = ""
    clearTimeout(this._filterTimer)
    this._applyFilter()
    this.inputTarget.focus()
  }
}
